import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Scale, AlertTriangle, TrendingDown } from 'lucide-react';

const LIVELLO = {
  alta: { cls: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', label: 'Rischio elevato' },
  media: { cls: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700', label: 'Attenzione' },
  nessuno: { cls: 'bg-white border-slate-200', badge: 'bg-emerald-100 text-emerald-700', label: 'Nessun rischio' },
};

export const TrendPesoCard = ({ ospiteId }) => {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/analisi/peso/${ospiteId}`).then(r => setD(r.data)).catch(() => setD(null));
  }, [ospiteId]);

  if (!d || !d.rilevazioni) return null;

  const stile = LIVELLO[d.rischio_livello] || LIVELLO.nessuno;
  const chartData = (d.serie || []).map(s => ({
    data: new Date(s.data).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }),
    peso: s.valore,
  }));

  return (
    <div className={`p-3 rounded-xl border ${stile.cls}`} data-testid="trend-peso-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <Scale className="w-4 h-4 text-brand" />Peso e stato nutrizionale
        </p>
        <Badge className={`text-[10px] ${stile.badge}`} data-testid="trend-peso-livello">{stile.label}</Badge>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mb-2">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400">Peso attuale</p>
          <p className="text-lg font-bold text-slate-900" data-testid="trend-peso-attuale">{d.peso_attuale} kg</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400">BMI</p>
          <p className="text-lg font-bold text-slate-900">
            {d.bmi ?? '-'}
            {d.bmi_categoria && <span className="text-[10px] font-normal text-slate-500 ml-1">{d.bmi_categoria}</span>}
          </p>
          {d.altezza_mancante && <p className="text-[10px] text-amber-600">inserisci l'altezza in anagrafica</p>}
        </div>
        {['30_giorni', '180_giorni'].map(k => {
          const v = d.variazioni?.[k];
          return (
            <div key={k}>
              <p className="text-[10px] uppercase tracking-wide text-slate-400">Variazione {k.replace('_', ' ')}</p>
              <p className={`text-lg font-bold ${v && v.delta_pct < 0 ? 'text-red-600' : 'text-slate-900'}`}>
                {v ? `${v.delta_kg > 0 ? '+' : ''}${v.delta_kg} kg` : '-'}
                {v && <span className="text-[10px] font-normal ml-1">({v.delta_pct > 0 ? '+' : ''}{v.delta_pct}%)</span>}
              </p>
            </div>
          );
        })}
      </div>

      {d.rischio_livello !== 'nessuno' && (
        <div className="text-xs mb-2 p-2 rounded-lg bg-white/70 border border-slate-200">
          <p className="font-semibold text-slate-800 flex items-center gap-1.5 mb-0.5">
            {d.rischio_livello === 'alta' ? <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> : <TrendingDown className="w-3.5 h-3.5 text-amber-600" />}
            Segnalazione al medico
          </p>
          <ul className="list-disc list-inside space-y-0.5 text-slate-600">
            {d.rischio_motivi.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      {chartData.length >= 2 && (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="data" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} unit=" kg" />
            <Tooltip contentStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="peso" stroke="#076633" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default TrendPesoCard;
